import type { PageDefinition } from "@judo/model-api";
import { PageContainerType } from "@judo/model-api";
import { Box, CircularProgress } from "@mui/material";
import { Suspense, lazy } from "react";
import { FormContainerErrorBoundary, TableContainerErrorBoundary, ViewContainerErrorBoundary } from "../errors";
import { resolveContainerType } from "../utils/resolve-container-type";

// Lazy-loaded chunks for code splitting by container type
const TableContainerRendererLazy = lazy(() => import("./TableContainerRenderer"));
const FormContainerRendererLazy = lazy(() => import("./FormContainerRenderer"));
const ViewContainerRendererLazy = lazy(() => import("./ViewContainerRenderer"));

export interface PageRendererProps {
	page: PageDefinition;
}

/**
 * Main page renderer that delegates to container type.
 */
export function PageRenderer({ page }: PageRendererProps) {
	// Container should be resolved to PageContainer object after model loading
	const container = page.container;
	if (typeof container === "string") {
		return (
			<Box p={2} data-testid={`page-renderer::${page.name}::unresolved`}>
				Container reference not resolved: {container}
			</Box>
		);
	}

	const effectiveType = resolveContainerType(container);

	// Use page ID as key to force unmount/remount on navigation
	const pageKey = page["xmi:id"] ?? page.name ?? "unknown-page";

	const LoadingFallback = (
		<Box display="flex" justifyContent="center" p={4} data-testid="page-renderer::loading">
			<CircularProgress />
		</Box>
	);

	switch (effectiveType) {
		case PageContainerType.TABLE:
			return (
				<TableContainerErrorBoundary element={container} key={pageKey}>
					<Suspense fallback={LoadingFallback}>
						<TableContainerRendererLazy page={page} />
					</Suspense>
				</TableContainerErrorBoundary>
			);

		case PageContainerType.FORM:
			return (
				<FormContainerErrorBoundary element={container} key={pageKey}>
					<Suspense fallback={LoadingFallback}>
						<FormContainerRendererLazy page={page} />
					</Suspense>
				</FormContainerErrorBoundary>
			);

		default:
			return (
				<ViewContainerErrorBoundary element={container} key={pageKey}>
					<Suspense fallback={LoadingFallback}>
						<ViewContainerRendererLazy page={page} />
					</Suspense>
				</ViewContainerErrorBoundary>
			);
	}
}
